import type {
  AggregationResultsCategoryRow,
  AggregationResultsSentimentRow,
} from '../../../../../../store/api/monitoring-api';
import {
  formatAspectsDynamicsBucketLabel,
  type AspectsDynamicsGroupingInterval,
  type AspectsDynamicsSentimentKey,
} from './build-aspects-dynamics-by-category-chart';

function sumAspectsField(
  row: AggregationResultsCategoryRow,
  field: 'positive_count' | 'neutral_count' | 'negative_count',
): number {
  return (row.aspects ?? []).reduce((s, a) => s + (Number(a[field]) || 0), 0);
}

const SERIES_NAMES: Record<AspectsDynamicsSentimentKey, string> = {
  positive: 'Позитивно',
  neutral: 'Нейтрально',
  negative: 'Негативно',
};

export type SentimentDynamicsBucketTotals = { pos: number; neu: number; neg: number };

/**
 * Суммарная тональность по всем категориям в каждом временном бакете (begin из API).
 * Те же бакеты, что у графика «Динамика тональности».
 */
export function aggregateSentimentDynamicsBuckets(
  rows: AggregationResultsCategoryRow[],
  groupingInterval: AspectsDynamicsGroupingInterval,
): { xCategories: string[]; buckets: SentimentDynamicsBucketTotals[] } {
  const bucketTotals = new Map<number, SentimentDynamicsBucketTotals>();
  const endByBegin = new Map<number, number>();

  for (const row of rows) {
    const begin = row.begin;
    if (begin == null || !Number.isFinite(begin)) continue;

    const cur = bucketTotals.get(begin) ?? { pos: 0, neu: 0, neg: 0 };
    cur.pos += sumAspectsField(row, 'positive_count');
    cur.neu += sumAspectsField(row, 'neutral_count');
    cur.neg += sumAspectsField(row, 'negative_count');
    bucketTotals.set(begin, cur);

    const end = row.end;
    if (end != null && Number.isFinite(end)) {
      endByBegin.set(begin, Math.max(endByBegin.get(begin) ?? end, end));
    }
  }

  const sortedBegins = [...bucketTotals.keys()].sort((a, b) => a - b);
  if (sortedBegins.length === 0) {
    return { xCategories: [], buckets: [] };
  }

  const xCategories = sortedBegins.map((b) =>
    formatAspectsDynamicsBucketLabel(b, endByBegin.get(b), groupingInterval),
  );

  const buckets = sortedBegins.map((b) => bucketTotals.get(b)!);

  return { xCategories, buckets };
}

/**
 * Бакеты из GET aggregation/results?type=sentiment (без дублирования по аспектам/категориям).
 */
export function aggregateSentimentDynamicsBucketsFromSentimentRows(
  sentimentRows: AggregationResultsSentimentRow[],
  groupingInterval: AspectsDynamicsGroupingInterval,
): { xCategories: string[]; buckets: SentimentDynamicsBucketTotals[] } {
  const bucketTotals = new Map<number, SentimentDynamicsBucketTotals>();
  const endByBegin = new Map<number, number>();

  for (const row of sentimentRows) {
    const begin = row.begin;
    if (begin == null || !Number.isFinite(begin)) continue;

    const cur = bucketTotals.get(begin) ?? { pos: 0, neu: 0, neg: 0 };
    cur.pos += Number(row.positive_count ?? 0);
    cur.neu += Number(row.neutral_count ?? 0);
    cur.neg += Number(row.negative_count ?? 0);
    bucketTotals.set(begin, cur);

    const end = row.end;
    if (end != null && Number.isFinite(end)) {
      endByBegin.set(begin, Math.max(endByBegin.get(begin) ?? end, end));
    }
  }

  const sortedBegins = [...bucketTotals.keys()].sort((a, b) => a - b);
  if (sortedBegins.length === 0) {
    return { xCategories: [], buckets: [] };
  }

  const xCategories = sortedBegins.map((b) =>
    formatAspectsDynamicsBucketLabel(b, endByBegin.get(b), groupingInterval),
  );

  const buckets = sortedBegins.map((b) => bucketTotals.get(b)!);

  return { xCategories, buckets };
}

export function buildSentimentDynamicsFromSentimentRows(
  sentimentRows: AggregationResultsSentimentRow[],
  groupingInterval: AspectsDynamicsGroupingInterval,
  sentiment: AspectsDynamicsSentimentKey,
): { xCategories: string[]; series: { name: string; data: number[] }[] } {
  const { xCategories, buckets } = aggregateSentimentDynamicsBucketsFromSentimentRows(
    sentimentRows,
    groupingInterval,
  );
  if (buckets.length === 0) {
    return { xCategories: [], series: [] };
  }

  const data = buckets.map((t) =>
    sentiment === 'positive' ? t.pos : sentiment === 'neutral' ? t.neu : t.neg,
  );

  return {
    xCategories,
    series: [{ name: SERIES_NAMES[sentiment], data }],
  };
}

/**
 * Суммарная тональность по всем категориям в каждом временном бакете (begin из API).
 */
export function buildSentimentDynamicsFromRows(
  rows: AggregationResultsCategoryRow[],
  groupingInterval: AspectsDynamicsGroupingInterval,
  sentiment: AspectsDynamicsSentimentKey,
): { xCategories: string[]; series: { name: string; data: number[] }[] } {
  const { xCategories, buckets } = aggregateSentimentDynamicsBuckets(rows, groupingInterval);
  if (buckets.length === 0) {
    return { xCategories: [], series: [] };
  }

  const data = buckets.map((t) =>
    sentiment === 'positive' ? t.pos : sentiment === 'neutral' ? t.neu : t.neg,
  );

  return {
    xCategories,
    series: [{ name: SERIES_NAMES[sentiment], data }],
  };
}
